/**
 * [STEP 2-A] GLB 자산 캐시 — URL 하나당 상태 하나(idle → loading → ready | error). 읽기 전용 렌더링.
 *
 *   · 같은 URL = 내려받기 · 해석 1회, 진행 중이면 같은 Promise 를 나눠 쓴다(장비 30대를 놓아도 한 번).
 *   · ready · error 로 바뀔 때만 구독자에게 한 번 알린다 → register.ts 가 그 URL 을 쓰는 정의를 다시 등록 → 3D 가 다시 만든다.
 *     이미 ready 인 URL 을 다시 물어도 알리지 않는다(레지스트리 변경 폭주 방지). error 는 그대로 남는다(다시 시도 안 함).
 *   · [STEP 2-B] 열쇠(identity)와 내려받기 주소(url)를 나눈다 — 열쇠는 assetResolver 가 정한다:
 *     운영 자산 = asset:<uuid>(서명 URL 이 바뀌어도 같은 자산) · 정적 = url:/twin-assets/….glb. 캐시는 열쇠로만 기억한다.
 *     ready 인 열쇠는 주소가 바뀌어도 다시 받지 않는다 — invalidateAsset(열쇠) 뒤에만 다시 받는다.
 *   · 주소 검사: 정적(url:) = 검사기 assetUrlIssue · 운영(asset:) = [STEP 2-C] 서버가 알린 Storage 출처(allowAssetOrigin)의
 *     /storage/v1/object/ 주소 또는 같은 출처의 정적 GLB 경로(개발 · 시험용). 그 밖(임의 외부 host)은 error.
 *   · [STEP 2-C] 불러오기 실패의 HTTP 상태(httpStatus)를 남긴다 — 400/401/403 이면 resolver 가 새 서명 주소를 한 번 받아 다시 받는다.
 *   · spec.build 는 동기 — peekAsset 으로 지금 상태를 보고 템플릿 복제 · 불러오는 중 상자 · 오류 상자를 바로 그린다(build.ts).
 *
 * 템플릿: 불러온 scene 을 그대로 두고(cloneAsset 로만 복제) 지오메트리 · 재질에 userData.shared — disposeTree 가 인스턴스를 지워도 원본은 남는다.
 *   Mesh · Group 만. SkinnedMesh 는 이번 단계 미지원 → error. AnimationClip 은 그리되 실행하지 않음(경고).
 *   노드 표 = 실제 Object3D.name → 객체(빈 이름 제외 · 같은 이름은 첫 번째만 · 경고). 이번엔 저장만(asset.nodes 연결은 STEP 2-B).
 *
 * 로더: 기본은 three 의 GLTFLoader 를 처음 쓸 때 동적 import(정의 런타임 · 테스트 · 서버 생성본이 정적으로 끌어오지 않게).
 *   테스트는 setGlbLoaderForTest 로 바꾼다.
 */
import * as THREE from "three";
import { assetUrlIssue } from "./validate";

export type AssetStatus = "idle" | "loading" | "ready" | "error";

export interface AssetTemplate {
  /** 원본 — 직접 붙이지 않는다(cloneAsset) */
  scene: THREE.Object3D;
  /** 원본 기준 노드 표(이름 → 객체) */
  nodes: Map<string, THREE.Object3D>;
  /** 원본 좌표의 경계 상자(m) */
  bbox: THREE.Box3;
  meshCount: number;
  animationCount: number;
}

export interface AssetSnapshot {
  /** 캐시 열쇠(asset:<id> · url:<경로>) */
  key: string;
  /** 마지막으로 내려받은(또는 받으려 한) 주소 */
  url: string;
  status: AssetStatus;
  template: AssetTemplate | null;
  error: string | null;
  warnings: string[];
  /** [STEP 2-C] 불러오기 실패의 HTTP 상태(있으면) — 서명 주소 만료(400/401/403) 판단용 */
  httpStatus: number | null;
}

/** 캐시에 넘기는 자산 — 열쇠(identity)와 내려받기 주소 */
export interface AssetRequest { key: string; url: string }

/** 로더 — URL → { scene, animations }. 테스트는 가짜로 바꾼다 */
export type GlbLoader = (url: string) => Promise<{ scene: THREE.Object3D; animations?: unknown[] }>;

interface Entry { status: AssetStatus; url: string; promise: Promise<AssetSnapshot> | null; template: AssetTemplate | null; error: string | null; warnings: string[]; httpStatus?: number | null }

const defaultLoader: GlbLoader = async (url) => {
  const { GLTFLoader } = await import("three/examples/jsm/loaders/GLTFLoader.js");
  const gltf = await new GLTFLoader().loadAsync(url);
  return { scene: gltf.scene, animations: gltf.animations };
};

let loader: GlbLoader = defaultLoader;
const entries = new Map<string, Entry>();
const listeners = new Set<(key: string, snap: AssetSnapshot) => void>();

/** 테스트 전용 — 로더를 바꾸고(null 이면 기본) 캐시를 비운다 */
export function setGlbLoaderForTest(fn: GlbLoader | null) { loader = fn || defaultLoader; entries.clear(); }
/** 테스트 전용 — 캐시만 비운다 */
export function resetAssetCacheForTest() { entries.clear(); }

/** ready · error 로 바뀔 때 한 번(그리고 nudgeAsset · invalidateAsset 때) — 인자는 열쇠 */
export function onAssetSettled(fn: (key: string, snap: AssetSnapshot) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

function snapOf(key: string, e: Entry | undefined): AssetSnapshot {
  return e ? { key, url: e.url, status: e.status, template: e.template, error: e.error, warnings: e.warnings, httpStatus: e.httpStatus ?? null } : { key, url: "", status: "idle", template: null, error: null, warnings: [], httpStatus: null };
}

/** [STEP 2-C] 운영 자산(asset:) 주소로 허용할 Storage 출처(서버 응답의 storage_origin — SUPABASE_URL) */
const allowedOrigins = new Set<string>();
export function allowAssetOrigin(origin: string | null | undefined) {
  if (typeof origin !== "string" || !/^https:\/\/[^/\s]+$/i.test(origin)) return;
  allowedOrigins.add(origin.toLowerCase());
}
/** 테스트 전용 */
export function resetAssetOriginsForTest() { allowedOrigins.clear(); }

/** 실패 오류에서 HTTP 상태 찾기(three FileLoader 의 HttpError.response.status · 메시지의 "responded with 403") */
function httpStatusOf(err: unknown): number | null {
  const r = err && typeof err === "object" ? (err as { response?: { status?: unknown }; status?: unknown }) : null;
  const direct = r && r.response && typeof r.response.status === "number" ? r.response.status : r && typeof r.status === "number" ? r.status : null;
  if (direct) return direct;
  const m = /responded with (\d{3})/.exec(err instanceof Error ? err.message : String(err));
  return m ? Number(m[1]) : null;
}

/** 지금 상태(불러오기를 시작하지 않는다) — 열쇠로 */
export function peekAsset(key: string): AssetSnapshot { return snapOf(key, entries.get(key)); }

/** 내려받기 주소 검사 — 정적(url:) 은 검사기 규칙 · 운영(asset:) 은 서버가 준 https:// 또는 같은 출처 경로 */
export function requestUrlIssue(r: AssetRequest): string | null {
  if (r.key.startsWith("url:")) return assetUrlIssue(r.url);
  if (r.key.startsWith("asset:")) {
    if (typeof r.url !== "string" || !r.url) return "내려받기 주소가 없습니다";
    if (/^https:\/\//i.test(r.url)) {
      let u: URL;
      try { u = new URL(r.url); } catch { return "자산 주소 형식이 올바르지 않습니다"; }
      if (!allowedOrigins.has(u.origin.toLowerCase())) return `허용되지 않은 자산 주소입니다(${u.host}) — 서버가 알린 Storage 주소만 씁니다`;
      if (!u.pathname.startsWith("/storage/v1/object/")) return "Storage 자산 경로(/storage/v1/object/)가 아닙니다";
      return null;
    }
    // 같은 출처의 정적 GLB(개발 · 시험 — 서명 흉내 ?… 허용). 경로 규칙은 정적 정의와 같다
    if (r.url.startsWith("/") && !r.url.startsWith("//")) return assetUrlIssue(r.url.split(/[?#]/)[0]) ? "같은 서버 주소는 /twin-assets/*.glb 만 씁니다" : null;
    return "자산 주소는 서버가 알린 Storage 주소 또는 /twin-assets/*.glb 만 씁니다";
  }
  return "모르는 자산 열쇠입니다";
}

/** 노드 표 — 실제 이름 그대로 · 빈 이름 제외 · 같은 이름은 첫 번째(onDup 으로 알림) */
export function buildNodeMap(root: THREE.Object3D, onDup?: (name: string) => void): Map<string, THREE.Object3D> {
  const map = new Map<string, THREE.Object3D>();
  root.traverse((o) => {
    if (!o.name) return;
    if (map.has(o.name)) { if (onDup) onDup(o.name); return; }
    map.set(o.name, o);
  });
  return map;
}

/** 불러온 scene → 템플릿. 지원하지 않는 것이 있으면 던진다 */
export function prepareTemplate(scene: THREE.Object3D, animations: unknown[] | undefined, warnings: string[]): AssetTemplate {
  let meshCount = 0;
  scene.traverse((o) => {
    if ((o as THREE.SkinnedMesh).isSkinnedMesh) throw new Error(`SkinnedMesh(「${o.name || "이름 없음"}」)는 아직 지원하지 않습니다`);
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    meshCount += 1;
    if (m.geometry) m.geometry.userData.shared = true; // disposeTree 가 인스턴스를 지워도 원본은 남는다
    const mats = Array.isArray(m.material) ? m.material : m.material ? [m.material] : [];
    for (const mat of mats) mat.userData.shared = true;
  });
  if (!meshCount) throw new Error("그릴 메시가 없습니다");
  const dups = new Set<string>();
  const nodes = buildNodeMap(scene, (n) => dups.add(n));
  if (dups.size) warnings.push(`같은 이름 노드가 있어 첫 번째만 씁니다: ${[...dups].slice(0, 5).join(", ")}`);
  const animationCount = Array.isArray(animations) ? animations.length : 0;
  if (animationCount) warnings.push(`GLB 안의 애니메이션 ${animationCount}개는 실행하지 않습니다(이번 단계)`);
  scene.updateMatrixWorld(true);
  const bbox = new THREE.Box3().setFromObject(scene);
  return { scene, nodes, bbox, meshCount, animationCount };
}

/** 장비 하나용 복제 — 위치 · 회전 · 크기 · 보이기는 따로, 지오메트리 · 재질은 원본과 나눠 쓴다(shared) */
export function cloneAsset(t: AssetTemplate): { object: THREE.Object3D; nodes: Map<string, THREE.Object3D> } {
  const object = t.scene.clone(true);
  return { object, nodes: buildNodeMap(object) };
}

/**
 * 불러오기(같은 열쇠면 같은 Promise). 주소가 검사에 걸리면 바로 error.
 *   settle(ready · error)에서 한 번만 알린다. 이미 ready · error 인 열쇠는 주소가 달라도 다시 받지 않는다(invalidateAsset 뒤에만).
 */
export function loadAsset(r: AssetRequest): Promise<AssetSnapshot> {
  const had = entries.get(r.key);
  if (had && had.promise) return had.promise;
  if (had && (had.status === "ready" || had.status === "error")) return Promise.resolve(snapOf(r.key, had));
  const issue = requestUrlIssue(r);
  if (issue) {
    const e: Entry = { status: "error", url: r.url, promise: null, template: null, error: issue, warnings: [] };
    entries.set(r.key, e);
    const snap = snapOf(r.key, e);
    // 알림은 늘 비동기 — build(동기) 안에서 불려도 레지스트리를 다시 건드리지 않게
    e.promise = Promise.resolve(snap).then((s2) => { console.warn(`[twin-asset] ${r.key} 쓸 수 없는 주소: ${issue}`); notify(r.key, s2); return s2; });
    return e.promise;
  }
  const e: Entry = { status: "loading", url: r.url, promise: null, template: null, error: null, warnings: [] };
  entries.set(r.key, e);
  e.promise = Promise.resolve()
    .then(() => loader(r.url))
    .then((res) => { e.template = prepareTemplate(res.scene, res.animations, e.warnings); e.status = "ready"; })
    .catch((err: unknown) => { e.status = "error"; e.template = null; e.error = err instanceof Error ? err.message : String(err); e.httpStatus = httpStatusOf(err); })
    .then(() => {
      const snap = snapOf(r.key, e);
      if (e.warnings.length) console.warn(`[twin-asset] ${r.key}: ${e.warnings.join(" · ")}`);
      if (e.status === "error") console.warn(`[twin-asset] ${r.key} 불러오기 실패: ${e.error}`);
      notify(r.key, snap);
      return snap;
    });
  return e.promise;
}

/** 지금 상태를 돌려주고, 처음 보는 열쇠면 불러오기를 시작한다(동기 — build 가 부른다) */
export function requestAsset(r: AssetRequest): AssetSnapshot {
  if (!entries.has(r.key)) void loadAsset(r);
  return peekAsset(r.key);
}

/**
 * 그 열쇠를 잊는다(다음 요청 때 다시 받는다) · 구독자에게 알려 쓰는 정의를 다시 그리게 한다. 전역 비우기는 두지 않는다.
 *   STEP 2-C: 서명 URL 만료(401/403) · 자산 교체 때 resolver 가 새 주소를 받은 뒤 부른다.
 */
export function invalidateAsset(key: string) {
  if (!entries.delete(key)) return;
  const snap = snapOf(key, undefined);
  void Promise.resolve().then(() => notify(key, snap));
}

/**
 * [STEP 2-B] 받을 수 없다고 확정된 열쇠를 error 로(resolver — 서버가 주소를 주지 않음 · 기다려도 주소가 오지 않음).
 *   ready · loading 이면 건드리지 않는다. 같은 이유로 이미 error 면 다시 알리지 않는다(재등록 반복 방지).
 */
export function failAsset(key: string, reason: string) {
  const had = entries.get(key);
  if (had && (had.status === "ready" || had.status === "loading")) return;
  if (had && had.status === "error" && had.error === reason) return;
  const e: Entry = { status: "error", url: "", promise: null, template: null, error: reason, warnings: [], httpStatus: null };
  entries.set(key, e);
  const snap = snapOf(key, e);
  e.promise = Promise.resolve(snap);
  console.warn(`[twin-asset] ${key}: ${reason}`);
  void Promise.resolve().then(() => notify(key, snap));
}

/** 아직 받지 않은 열쇠에 주소가 생겼을 때(resolver) — 구독자가 다시 그리게 알린다. ready · error · loading 이면 아무것도 안 함 */
export function nudgeAsset(key: string) {
  if (entries.has(key)) return;
  const snap = snapOf(key, undefined);
  void Promise.resolve().then(() => notify(key, snap));
}

function notify(key: string, snap: AssetSnapshot) {
  listeners.forEach((fn) => { try { fn(key, snap); } catch (err) { console.warn("[twin-asset] 알림 처리 실패", err); } });
}
